import React from 'react';
import { SpeedReadingCourse } from './trainingApi';

interface CourseGridProps {
  courses: SpeedReadingCourse[];
  canManage: boolean;
  onSelect: (course: SpeedReadingCourse) => void;
  onEdit?: (course: SpeedReadingCourse) => void;
  onDelete?: (course: SpeedReadingCourse) => void;
}

const LESSON_STATUS_COLORS: Record<string, string> = {
  READY: 'bg-emerald-400',
  PROCESSING: 'bg-blue-400',
  DRAFT: 'bg-amber-400',
  FAILED: 'bg-red-400',
};

const CourseCard: React.FC<{
  course: SpeedReadingCourse;
  canManage: boolean;
  onSelect: (course: SpeedReadingCourse) => void;
  onEdit?: (course: SpeedReadingCourse) => void;
  onDelete?: (course: SpeedReadingCourse) => void;
}> = ({ course, canManage, onSelect, onEdit, onDelete }) => {
  const lessonCount = course.lessons?.length || 0;
  const readyCount = course.lessons?.filter((l) => l.status === 'READY').length || 0;

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-[1.75rem] border border-[#dbe7ec] bg-white shadow-[0_2px_16px_rgba(23,78,103,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_56px_rgba(23,78,103,0.14)]">
      <button
        type="button"
        onClick={() => onSelect(course)}
        className="relative h-48 w-full overflow-hidden text-left"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2e42] via-[#0e6a7d] to-[#174e67]" />
        {course.coverMedia?.url ? (
          <img
            src={course.coverMedia.url}
            alt={course.title}
            className="absolute inset-0 h-full w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-[1.06] group-hover:opacity-90"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/10 text-white/40 ring-1 ring-white/10">
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
            </div>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />

        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold text-white backdrop-blur-sm ring-1 ring-white/15">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11a9 9 0 1 1 18 0v3a9 9 0 1 1-18 0z"/>
                <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z"/>
              </svg>
              {lessonCount} ders
            </span>
            {readyCount > 0 && readyCount < lessonCount ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/25 px-3 py-1 text-[11px] font-bold text-emerald-200 backdrop-blur-sm ring-1 ring-emerald-400/30">
                {readyCount} hazır
              </span>
            ) : readyCount === lessonCount && lessonCount > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/25 px-3 py-1 text-[11px] font-bold text-emerald-200 backdrop-blur-sm ring-1 ring-emerald-400/30">
                Tümü hazır
              </span>
            ) : null}
          </div>
        </div>

        <div className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#174e67] shadow-[0_6px_20px_rgba(0,0,0,0.2)] transition-transform duration-300 group-hover:scale-110">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"/>
            <path d="m12 5 7 7-7 7"/>
          </svg>
        </div>
      </button>

      <div className="flex flex-1 flex-col px-5 py-4">
        <h3 className="text-[15px] font-extrabold leading-snug tracking-tight text-[#174e67] line-clamp-2">{course.title}</h3>
        {course.description ? (
          <p className="mt-1.5 text-[13px] leading-5 text-[#5d7681] line-clamp-2">{course.description}</p>
        ) : null}

        {lessonCount > 0 ? (
          <div className="mt-3 flex items-center gap-1.5">
            {course.lessons?.slice(0, 8).map((lesson) => (
              <span
                key={lesson.id}
                title={lesson.title}
                className={`h-1.5 flex-1 rounded-full ${LESSON_STATUS_COLORS[lesson.status] || 'bg-gray-300'} opacity-70`}
              />
            ))}
            {lessonCount > 8 ? (
              <span className="text-[10px] font-bold text-[#9fb3bc]">+{lessonCount - 8}</span>
            ) : null}
          </div>
        ) : (
          <p className="mt-3 text-[12px] font-semibold text-[#9fb3bc]">Henüz ders eklenmemiş</p>
        )}

        <div className="flex-1" />

        {canManage ? (
          <div className="mt-4 flex items-center gap-1.5 border-t border-[#ecf2f5] pt-3.5">
            <button
              type="button"
              onClick={() => onEdit?.(course)}
              className="rounded-full bg-[#eef6f8] px-3 py-1.5 text-[11px] font-bold text-[#174e67] transition hover:bg-[#dff0f3]"
            >
              Düzenle
            </button>
            <button
              type="button"
              onClick={() => onDelete?.(course)}
              className="ml-auto rounded-full bg-[#fef4f4] px-3 py-1.5 text-[11px] font-bold text-[#a04c4c] transition hover:bg-[#fde0e0]"
            >
              Sil
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const CourseGrid: React.FC<CourseGridProps> = ({ courses, canManage, onSelect, onEdit, onDelete }) => {
  if (courses.length === 0) {
    return (
      <div className="rounded-[1.75rem] border border-dashed border-[#cfe2e8] bg-white px-10 py-24 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0ea5b7]">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          </svg>
        </div>
        <p className="text-base font-bold text-[#174e67]">Henüz ana ders yok</p>
        <p className="mt-2 text-sm leading-6 text-[#67808c]">
          {canManage
            ? '"Yeni Ana Ders" butonundan ilk kursu oluşturabilirsin.'
            : 'Yöneticiler kurs oluşturduğunda burada görünecek.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {courses.map((course) => (
        <CourseCard
          key={course.id}
          course={course}
          canManage={canManage}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default CourseGrid;
